import { useEffect, useMemo, useState } from "react";
import "./DeleteModal.css";
import "./DeeplinkModal.css";
import { Box } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "../buttonComponent/ButtonComponent";
import TextInput from "../textInput/TextInput";
import DeeplinkModalHeader from "./DeeplinkModalHeader";
import DeeplinkInstitutionPicker from "./DeeplinkInstitutionPicker";
import { useAuth } from "../../config/utilities/useAuth";
import { APICreateDeeplink } from "../../config/utilities/APIs/masterAdmin/deeplinksApi";

const boxStyle = {
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  width: "min(52rem, calc(100vw - 2rem))",
  overflow: "hidden",
  maxHeight: "92vh",
};

const isValidUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export default function CreateDeeplinkModal({
  isOpen,
  onClose,
  institutions = [],
  onCreated,
  onError,
}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState({
    label: "",
    url: "",
    institutionIds: [],
  });
  const [keyUp, setKeyUp] = useState(false);
  const [urlError, setUrlError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setInput({
      label: "",
      url: "",
      institutionIds: [],
    });
    setUrlError("");
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
    if (name === "url") setUrlError("");
  };

  const isFormValid = useMemo(() => {
    const label = input.label?.trim() || "";
    const url = input.url?.trim() || "";
    return label.length > 0 && label.length <= 60 && isValidUrl(url);
  }, [input]);

  const handleCreate = async () => {
    const label = input.label.trim();
    const url = input.url.trim();

    if (!isValidUrl(url)) {
      setUrlError("Enter a valid http or https URL");
      return;
    }

    const body = {
      url,
      institutionIds: input.institutionIds || [],
      label,
    };

    const response = await APICreateDeeplink(body, setLoading, token);
    if (response?.status >= 200 && response?.status < 300) {
      onCreated?.();
      onClose();
      return;
    }

    const message =
      response?.response?.data?.message ||
      response?.message ||
      "Failed to create deeplink";
    onError?.(message);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <Box sx={boxStyle} className="deeplink-modal-box">
        <DeeplinkModalHeader
          title="Create Deeplink"
          subtitle="Add a sidebar link for one or more institution workspaces."
          onClose={onClose}
        />

        <div className="user-modal-content deeplink-modal-content">
          <section className="deeplink-modal-section deeplink-modal-details">
            <div className="deeplink-modal-section-header">
              <h3 className="deeplink-modal-section-title">Deeplink details</h3>
              <p className="deeplink-modal-section-hint">
                The label and URL shown in each institution&apos;s admin sidebar.
              </p>
            </div>
            <div className="deeplink-modal-section-body">
              <TextInput
                label="Label"
                placeholder="e.g. Parent portal"
                input={input}
                setInput={setInput}
                name="label"
                onChangeFunction={handleInputChange}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                required
              />
              <TextInput
                label="URL"
                placeholder="https://school.busbuddy.app/parent-portal"
                input={input}
                setInput={setInput}
                name="url"
                onChangeFunction={handleInputChange}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                error={Boolean(urlError)}
                errorMessage={urlError}
                required
              />
            </div>
          </section>

          <section className="deeplink-modal-section deeplink-modal-assignments">
            <div className="deeplink-modal-section-header">
              <h3 className="deeplink-modal-section-title">Linked institutions</h3>
              <p className="deeplink-modal-section-hint">
                Optional. Leave empty to keep this deeplink unassigned.
              </p>
            </div>
            <div className="deeplink-modal-section-body">
              <DeeplinkInstitutionPicker
                institutions={institutions}
                selectedIds={input.institutionIds || []}
                onChange={(institutionIds) =>
                  setInput((prev) => ({ ...prev, institutionIds }))
                }
                isOpen={isOpen}
              />
            </div>
          </section>
        </div>

        <div className="button-container-delete">
          <SecondaryButton
            width="14rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={onClose}
          />
          <PrimaryButton
            width="14rem"
            label="Create Deeplink"
            onClickFunction={handleCreate}
            disable={!isFormValid}
            loading={loading}
          />
        </div>
      </Box>
    </div>
  );
}
